import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import { EntityTypeSupportingGroups, Group } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import User from "@mapmycustomers/shared/types/User";
import { Modal } from "@mapmycustomers/ui";

import { isCreatingGroup } from "@app/store/groups";
import { createGroup } from "@app/store/groups/actions";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { TOPMOST_MODAL_ZINDEX } from "@app/util/consts";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import Footer from "./components/Footer";
import Form from "./components/Form";
import styles from "./CreateGroupModal.module.scss";

export interface CreateGroupModalProps {
  entityType: EntityTypeSupportingGroups;
  onCreate?: (group: Group, addItems?: boolean) => void;
  onHide: (created: boolean, addItems?: boolean) => void;
  showCreateOptions?: boolean;
  visible: boolean;
}

const CreateGroupModal = ({
  createGroup,
  creating,
  entityType,
  onCreate,
  onHide,
  showCreateOptions,
  visible,
}: CreateGroupModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics(
    ["Create Group", getEntityTypeAnalyticsName(entityType, false, false)],
    useAnalytics()
  );
  useEffect(() => {
    if (visible) {
      analytics.initiated();
    }
  }, [analytics, visible]);

  const [groupName, setGroupName] = useState<string>("");
  useEffect(() => {
    if (visible) {
      setGroupName("");
    }
  }, [visible]);

  const [selectedColor, setSelectedColor] = useState<AnyColor | undefined>();
  const [cadenceInterval, setCadenceInterval] = useState<false | number>(false);
  const [selectedActivityTypeIds, setSelectedActivityTypeIds] = useState<ActivityType["id"][]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<User["id"][]>([]);

  const handleCancelModal = useCallback(() => {
    onHide(false);
    analytics.cancelled("");
  }, [analytics, onHide]);

  const handleCreate = useCallback(
    (createAndAdd?: boolean) => {
      return createGroup({
        activityTypesIds: selectedActivityTypeIds,
        cadenceInterval: cadenceInterval || undefined,
        color: selectedColor,
        entityType,
        name: groupName,
        onSuccess: (group: Group) => {
          onCreate?.(group, createAndAdd === true);
          onHide(true, createAndAdd === true);
          analytics.completed("", {
            cadenceInterval,
            sharingWithUsers: selectedUserIds.length > 0,
          });
        },
        userIdsToShareWith: selectedUserIds,
      });
    },
    [
      analytics,
      cadenceInterval,
      createGroup,
      selectedColor,
      entityType,
      groupName,
      onCreate,
      onHide,
      selectedActivityTypeIds,
      selectedUserIds,
    ]
  );

  const handleCreateAndAdd = useCallback(() => handleCreate(true), [handleCreate]);

  return (
    <Modal
      className={styles.modal}
      footer={
        <Footer
          creating={creating}
          entityType={entityType}
          onCancel={handleCancelModal}
          onCreate={handleCreate}
          onCreateAndAdd={handleCreateAndAdd}
          showCreateOptions={showCreateOptions}
        />
      }
      onCancel={handleCancelModal}
      open={visible}
      title={intl.formatMessage(
        {
          id: "createGroupModal.title",
          defaultMessage:
            "Create New{entityType, select, accounts { Company} contacts { Person} deals { Deal} other {}} Group",
          description: "Title of the Create Group modal",
        },
        {
          entityType,
        }
      )}
      zIndex={TOPMOST_MODAL_ZINDEX}
    >
      <Form
        cadence={cadenceInterval}
        cadenceActivityTypes={selectedActivityTypeIds}
        color={selectedColor}
        name={groupName}
        onCadenceActivityTypesChange={setSelectedActivityTypeIds}
        onCadenceChange={setCadenceInterval}
        onColorChange={setSelectedColor}
        onNameChange={setGroupName}
        onSharedUsersChange={setSelectedUserIds}
        userIds={selectedUserIds}
      />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  creating: isCreatingGroup(state),
});

const mapDispatchToProps = {
  createGroup: createGroup.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateGroupModal);
